import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ModuleKey, Permission } from '@tengesa/shared';
import './styles.css';
import { ApiError } from './lib/api';
import { SessionProvider, useMeQuery, useSession, useSessionEvents } from './app/session';
import { Shell } from './app/Shell';
import { ErrorNotice, Spinner, ToastProvider } from './ui';
import { LoginPage } from './features/auth/LoginPage';
import { ForcePasswordChange } from './features/auth/ForcePasswordChange';
import { OverviewPage } from './features/overview/OverviewPage';
import { HomePage } from './features/overview/HomePage';
import { StaffPage } from './features/staff/StaffPage';
import { RolesPage } from './features/roles/RolesPage';
import { BranchesPage } from './features/branches/BranchesPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { AuditPage } from './features/audit/AuditPage';
import { AccountPage } from './features/account/AccountPage';
import { PosPage } from './features/pos/PosPage';
import { SalesPage } from './features/sales/SalesPage';
import { SaleDetailPage } from './features/sales/SaleDetailPage';
import { ProductsPage } from './features/products/ProductsPage';
import { SuppliersPage } from './features/suppliers/SuppliersPage';
import { PurchasingPage } from './features/purchasing/PurchasingPage';
import { StockPage } from './features/stock/StockPage';
import { StocktakesPage, StocktakeDetailPage } from './features/stock/StocktakesPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { ShiftsPage, ShiftDetailPage } from './features/shifts/ShiftsPage';
import { CustomersPage, CustomerDetailPage } from './features/customers/CustomersPage';
import { ExpensesPage } from './features/expenses/ExpensesPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { ExpiryPage } from './features/expiry/ExpiryPage';
import { PromotionsPage } from './features/promotions/PromotionsPage';
import { WastagePage } from './features/wastage/WastagePage';
import { BreakdownsPage } from './features/breakdowns/BreakdownsPage';
import { OrdersPage, OrderDetailPage } from './features/orders/OrdersPage';
import { DeliveriesPage } from './features/deliveries/DeliveriesPage';
import { ExchangeRatesPage } from './features/currency/ExchangeRatesPage';
import { FiscalPage } from './features/fiscal/FiscalPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

function Guard({ any, module, children }: { any: Permission[]; module?: ModuleKey; children: ReactNode }) {
  const { canAny, hasModule } = useSession();
  return canAny(...any) && (!module || hasModule(module)) ? <>{children}</> : <Navigate to="/" replace />;
}

function Home() {
  const { can } = useSession();
  return can('dashboard.view') ? <DashboardPage /> : <HomePage />;
}

function Root() {
  useSessionEvents();
  const me = useMeQuery();
  if (me.isLoading) return <Spinner label="Starting Tengesa" />;
  if (me.error)
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorNotice error={me.error} onRetry={() => me.refetch()} />
      </div>
    );
  if (!me.data) return <LoginPage />;
  if (me.data.user.mustChangePassword) return <ForcePasswordChange name={me.data.user.fullName} />;
  return (
    <SessionProvider me={me.data}>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="staff" element={<Guard any={['users.view']}><StaffPage /></Guard>} />
          <Route path="roles" element={<Guard any={['roles.manage']}><RolesPage /></Guard>} />
          <Route path="branches" element={<Guard any={['branches.manage']}><BranchesPage /></Guard>} />
          <Route path="settings" element={<Guard any={['settings.manage']}><SettingsPage /></Guard>} />
          <Route path="audit" element={<Guard any={['audit.view']}><AuditPage /></Guard>} />
          <Route path="account" element={<AccountPage />} />
          <Route path="pos" element={<Guard any={['pos.sell']}><PosPage /></Guard>} />
          <Route path="sales" element={<Guard any={['pos.sell', 'reports.sales', 'pos.return', 'pos.void_sale']}><SalesPage /></Guard>} />
          <Route path="sales/:id" element={<Guard any={['pos.sell', 'reports.sales', 'pos.return', 'pos.void_sale']}><SaleDetailPage /></Guard>} />
          <Route path="products" element={<Guard any={['products.view', 'products.manage', 'stock.view']}><ProductsPage /></Guard>} />
          <Route path="suppliers" element={<Guard any={['suppliers.view', 'suppliers.manage']}><SuppliersPage /></Guard>} />
          <Route path="purchasing" element={<Guard any={['purchases.manage', 'stock.receive']}><PurchasingPage /></Guard>} />
          <Route path="stock" element={<Guard any={['stock.view', 'stock.adjust', 'stock.transfer']}><StockPage /></Guard>} />
          <Route path="stocktakes" element={<Guard any={['stock.stocktake', 'stock.stocktake_approve']}><StocktakesPage /></Guard>} />
          <Route path="stocktakes/:id" element={<Guard any={['stock.stocktake', 'stock.stocktake_approve']}><StocktakeDetailPage /></Guard>} />
          <Route path="activity" element={<Guard any={['dashboard.view']}><OverviewPage /></Guard>} />
          <Route path="shifts" element={<Guard any={['shifts.operate', 'shifts.reconcile']}><ShiftsPage /></Guard>} />
          <Route path="shifts/:id" element={<Guard any={['shifts.operate', 'shifts.reconcile']}><ShiftDetailPage /></Guard>} />
          <Route path="customers" element={<Guard any={['customers.view', 'customers.manage', 'credit.receive_payment']}><CustomersPage /></Guard>} />
          <Route path="customers/:id" element={<Guard any={['customers.view', 'customers.manage', 'credit.receive_payment']}><CustomerDetailPage /></Guard>} />
          <Route path="expenses" element={<Guard any={['expenses.record', 'expenses.approve', 'reports.financial']}><ExpensesPage /></Guard>} />
          <Route path="reports" element={<Guard any={['reports.sales', 'reports.profit', 'reports.stock', 'reports.financial']}><ReportsPage /></Guard>} />
          <Route path="expiry" element={<Guard module="expiry" any={['expiry.manage', 'stock.view']}><ExpiryPage /></Guard>} />
          <Route path="promotions" element={<Guard module="promotions" any={['promotions.manage']}><PromotionsPage /></Guard>} />
          <Route path="wastage" element={<Guard module="wastage" any={['wastage.record', 'reports.stock']}><WastagePage /></Guard>} />
          <Route path="breakdowns" element={<Guard module="yields" any={['yields.manage', 'reports.stock']}><BreakdownsPage /></Guard>} />
          <Route path="orders" element={<Guard module="orders" any={['orders.manage', 'deliveries.manage', 'customers.view']}><OrdersPage /></Guard>} />
          <Route path="orders/:id" element={<Guard module="orders" any={['orders.manage', 'deliveries.manage', 'customers.view']}><OrderDetailPage /></Guard>} />
          <Route path="deliveries" element={<Guard module="deliveries" any={['deliveries.manage']}><DeliveriesPage /></Guard>} />
          <Route path="exchange-rates" element={<Guard any={['currency.rates_manage', 'settings.manage', 'reports.financial']}><ExchangeRatesPage /></Guard>} />
          <Route path="fiscal" element={<Guard any={['fiscal.manage']}><FiscalPage /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
